import { createUUID } from "reportengine/utils";
import {
  EqualComparator,
  TBorder,
  TBox,
  TItem,
  TPickItem,
  TResizeVector,
  TTransform,
  TTransformMode,
  TTransformReason,
} from "./types";
const createItem = (x: number, y: number, width: number, height: number) => {
  return {
    id: `${createUUID()}`,
    x,
    y,
    width,
    height,
  };
};

const fromItem = (item: TItem): TBox => {
  return {
    ...item,
    left: item.x,
    top: item.y,
    bottom: item.y + item.height,
    right: item.x + item.width,
    vcenter: item.y + item.height * 0.5,
    hcenter: item.x + item.width * 0.5,
  };
};

const create = (x: number, y: number, width: number, height: number) => {
  return fromItem(createItem(x, y, width, height));
};

const centerX = (item: TBox) => {
  return item.x + item.width * 0.5;
};

const centerY = (item: TBox) => {
  return item.y + item.height * 0.5;
};

const pick = (item: TBox) => (boxList: TBox[], pickValue: TPickItem) => {
  const results: TBox[] = [];
  for (let i = 0; i < boxList.length; i += 1) {
    const newValue = pickValue(boxList[i], item);
    if (!newValue && results.length > 0) {
      break;
    }
    if (newValue) {
      results.push(newValue);
    }
  }
  return results;
};

/**
 *
 * Calcualte distances from the item 1 border to opposing item 2 border
 *    --------        ----------
 *   | item 2 | <--> |  item 1  |
 *    --------        ----------
 * The above might return something like {left:10, bottom:0, top:0, right: -100}
 *    --------
 *   | item 1 |
 *    --------
 *                --------
 *               | item 2 |
 *                --------
 * The above might return something like {left:-100, bottom:20, top:-100, right: 20}
 *
 * @param item1 item which is compared to other item
 * @param item2
 * @param border
 * @returns
 */

type TBorderDistances = ReturnType<typeof borderDistance>;
export const borderDistance = (item1: TBox, item2: TBox) => {
  return {
    item1,
    item2,
    left: item1.left - item2.right,
    right: item2.left - item1.right,
    top: item1.top - item2.bottom,
    bottom: item2.top - item1.bottom,
  };
};
export const borderDistanceToDirection = (
  item1: TBox,
  item2: TBox,
  direction: TBorder
) => {
  switch (direction) {
    case "left":
      return item1.left - item2.right;
    case "right":
      return item2.left - item1.right;
    case "top":
      return item1.top - item2.bottom;
    case "bottom":
      return item2.top - item1.bottom;
    default:
      return 0;
  }
};

type TCandidateStep = {
  step: number;
  item: TBorderDistances;
  next: { candidates: Array<TCandidateStep | null> };
};

export const buildPositiveStepsFromItem = (
  items: TBorderDistances[],
  direction: TBorder,
  step?: number
) => {
  const item = items[0];
  const rest = items.slice(1);
  const candidates = rest
    .map<TCandidateStep | null>((rItem, index) => {
      const dist = borderDistanceToDirection(
        item.item2,
        rItem.item2,
        direction
      );
      if (dist > 0 && (!step || dist === step)) {
        return {
          step: dist,
          item: borderDistance(item.item2, rItem.item2),
          next: buildPositiveStepsFromItem(rest.slice(index), direction, dist),
        };
      }
      return null;
    })
    .filter((v) => v) as Array<TCandidateStep | null>;
  return {
    candidates,
  };
};

/**
 *
 * Find elements which have similar distance to some direction, to possibly divide items equally
 *
 * @param item
 * @param distances
 * @param direction
 * @param limit
 * @param isEqualEnough
 * @returns
 */
export const getStepsToDirection = (
  item: TBox,
  distances: TBorderDistances[],
  direction: TBorder,
  limit: [TBorder, TBorder],
  isEqualEnough: EqualComparator
): Array<TCandidateStep> => {
  const itemsToExamine = distances.filter(
    (dist) => dist[limit[0]] < 0 && dist[limit[1]] < 0
  );

  const itemsToDirection = itemsToExamine
    .filter((d) => d[direction] > 0)
    .sort((a, b) => a[direction] - b[direction]);

  return buildPositiveStepsFromItem(
    itemsToDirection,
    direction
  ).candidates.filter((value) => {
    // console.log('value', value);
    if (!value) return false;
    // console.log('value filter ', similar(value.item.item1.right - item.left, value.step));

    if (direction === "bottom") {
      return isEqualEnough(value.item.item1.top - item.bottom, value.step);
    }
    if (direction === "left") {
      return isEqualEnough(item.left - value.item.item1.right, value.step);
    }
    if (direction === "right") {
      return isEqualEnough(value.item.item1.left - item.right, value.step);
    }
    if (direction === "top") {
      return isEqualEnough(item.top - value.item.item1.bottom, value.step);
    }
    return false;
  }) as Array<TCandidateStep>;
};

/**
 *
 * The function returns suggestion for "snap to grid"
 *
 * @param item
 * @param list
 * @param isEqualEnough
 * @param mode
 * @returns
 */
const pickTransformed = (
  item: TBox,
  list: TBox[],
  isEqualEnough: EqualComparator,
  mode: TTransformMode
) => {
  const results: TTransform[] = [];

  const distances = list.map((other) => borderDistance(item, other));
  const getOrCreateTransform = (vector: Partial<TResizeVector>) => {
    const old = results.find((v) => {
      const d = v.delta;
      return (
        d.height === vector.height &&
        d.width === vector.width &&
        d.x === vector.x &&
        d.y === vector.y
      );
    });
    if (old) return old;
    const newTransform: TTransform = {
      delta: vector,
      grids: [],
    };
    results.push(newTransform);
    return newTransform;
  };

  const addTransform = (
    vector: Partial<TResizeVector>,
    gridItem: TBox,
    type: TTransformReason
  ) => {
    const t = getOrCreateTransform(vector);

    t.grids.push({
      mode,
      type,
      gridItems: [gridItem],
    });
  };

  /**
   * y changes
   */
  const yTransform = (
    item: TBox,
    gridItem: TBox,
    key: "top" | "bottom" | "vcenter"
  ) => {
    if (isEqualEnough(gridItem[key], item[key]))
      addTransform({ y: gridItem[key] - item[key] }, gridItem, key);
  };

  /**
   * x changes
   */
  const xTransform = (
    item: TBox,
    gridItem: TBox,
    key: "left" | "right" | "hcenter"
  ) => {
    if (isEqualEnough(gridItem[key], item[key]))
      addTransform({ x: gridItem[key] - item[key] }, gridItem, key);
  };

  /**
   * x changes, width is adjusted so that right border does not move
   */
  const xwTransform = (
    item: TBox,
    gridItem: TBox,
    key: "left" | "right" | "hcenter"
  ) => {
    if (isEqualEnough(gridItem[key], item[key]))
      addTransform(
        { x: gridItem[key] - item[key], width: item[key] - gridItem[key] },
        gridItem,
        key
      );
  };

  /**
   * y changes, height is adjusted so that right border does not move
   */
  // { y: gridItem[key] - item[key], height: item[key] - gridItem[key] },

  const yhTransform = (
    item: TBox,
    gridItem: TBox,
    key: "top" | "bottom" | "vcenter"
  ) => {
    if (isEqualEnough(gridItem[key], item[key]))
      addTransform(
        { y: gridItem[key] - item[key], height: item[key] - gridItem[key] },
        gridItem,
        key
      );
  };

  /**
   * only width changes
   */
  const wTransform = (
    item: TBox,
    gridItem: TBox,
    key: "left" | "right" | "hcenter"
  ) => {
    if (isEqualEnough(gridItem[key], item[key]))
      addTransform({ width: gridItem[key] - item[key] }, gridItem, key);
  };

  /**
   * only height changes
   */
  const hTransform = (
    item: TBox,
    gridItem: TBox,
    key: "top" | "bottom" | "vcenter"
  ) => {
    if (isEqualEnough(gridItem[key], item[key]))
      addTransform({ height: gridItem[key] - item[key] }, gridItem, key);
  };

  /**
   *
   * Creates suggestions for transforms based on distances between boxes.
   *
   * @param direction
   * @param limit
   * @param reason
   */
  const distanceMap = (
    direction: TBorder,
    limit: [TBorder, TBorder],
    reason: TTransformReason
  ) => {
    getStepsToDirection(
      item,
      distances,
      direction,
      limit,
      isEqualEnough
    ).forEach((cand) => {
      // console.log('cand', cand);
      const allItems = [cand.item.item1, cand.item.item2];
      let { next } = cand;
      while (next.candidates[0]) {
        allItems.push(next.candidates[0].item.item2);
        next = next.candidates[0].next;
      }
      const t = (() => {
        switch (direction) {
          case "right":
            return getOrCreateTransform({
              x: cand.item.item1.left - item.right - cand.step,
            });
          case "left":
            return getOrCreateTransform({
              x: cand.item.item1.right - item.left + cand.step,
            });
          case "bottom":
            return getOrCreateTransform({
              y: cand.item.item1.top - item.bottom - cand.step,
            });
          case "top":
            return getOrCreateTransform({
              y: cand.item.item1.bottom - item.top + cand.step,
            });
          default:
            return null;
        }
      })();
      // console.log('cand vector ', t, direction, item.top);
      t?.grids.push({ mode, type: reason, gridItems: allItems });
    });
  };

  /**
   * If the transform mode is "move" it is possible to transform x and y coordinates based on the difference of
   * the elements value compared to the grid items value
   */
  if (mode === "move") {
    list.forEach((gridItem) => {
      xTransform(item, gridItem, "left");
      xTransform(item, gridItem, "right");
      xTransform(item, gridItem, "hcenter");
      yTransform(item, gridItem, "top");
      yTransform(item, gridItem, "bottom");
      yTransform(item, gridItem, "vcenter");
    });

    distanceMap("top", ["left", "right"], "distTop");
    distanceMap("bottom", ["left", "right"], "distBottom");
    distanceMap("left", ["top", "bottom"], "distLeft");
    distanceMap("right", ["top", "bottom"], "distRight");
  }
  /**
   * If element is moved only to the x direction then following grid events are available
   */
  if (mode === "movex") {
    list.forEach((gridItem) => {
      xTransform(item, gridItem, "left");
      xTransform(item, gridItem, "right");
      xTransform(item, gridItem, "hcenter");
      distanceMap("left", ["top", "bottom"], "distLeft");
      distanceMap("right", ["top", "bottom"], "distRight");
    });
  }

  /**
   * If element is moved only to the y direction then following grid events are available
   */
  if (mode === "movey") {
    list.forEach((gridItem) => {
      yTransform(item, gridItem, "top");
      yTransform(item, gridItem, "bottom");
      yTransform(item, gridItem, "vcenter");
      distanceMap("top", ["left", "right"], "distTop");
      distanceMap("bottom", ["left", "right"], "distBottom");
    });
  }

  /**
   * resize from the left border, adjust both x and width
   */
  if (mode === "resizeLeft") {
    list.forEach((gridItem) => {
      xwTransform(item, gridItem, "left");
    });
  }

  /**
   * resize from the top left corner, adjust x,y and width and height
   */
  if (mode === "resizeTopLeft") {
    list.forEach((gridItem) => {
      xwTransform(item, gridItem, "left");
      yhTransform(item, gridItem, "top");
    });
  }

  /**
   * resize from the top border, adjust y and height
   */
  if (mode === "resizeTop") {
    list.forEach((gridItem) => {
      yhTransform(item, gridItem, "top");
    });
  }

  /**
   * resize from the top right corner, adjust y and height or width
   */
  if (mode === "resizeTopRight") {
    list.forEach((gridItem) => {
      yhTransform(item, gridItem, "top");
      wTransform(item, gridItem, "right");
    });
  }

  /**
   * resize from the right border, adjust width
   */
  if (mode === "resizeRight") {
    list.forEach((gridItem) => {
      wTransform(item, gridItem, "right");
    });
  }

  /**
   * resize from the bottom right corner, adjust width and height
   */
  if (mode === "resizeBottomRight") {
    list.forEach((gridItem) => {
      hTransform(item, gridItem, "bottom");
      wTransform(item, gridItem, "right");
    });
  }

  /**
   * resize from the bottom border, adjust height
   */
  if (mode === "resizeBottom") {
    list.forEach((gridItem) => {
      hTransform(item, gridItem, "bottom");
    });
  }

  /**
   * resize from the bottom left corner, x and width and height
   */
  if (mode === "resizeBottomLeft") {
    list.forEach((gridItem) => {
      xwTransform(item, gridItem, "left");
      hTransform(item, gridItem, "bottom");
    });
  }

  /**
   * Return only results which are not conflicting with each other, for example if there is
   * one transform changing y value then another transforms can not do different transform for y
   * at the same time.
   */
  let suggestedDelta: Partial<TResizeVector> = {};
  const filteredResults: TTransform[] = [];
  const bestResults = results.sort((a, b) => b.grids.length - a.grids.length);
  const usedKeys: Record<string, boolean> = {};
  for (let i = 0; i < bestResults.length; i += 1) {
    const r = bestResults[i];
    let didConflict = false;
    Object.keys(r.delta).forEach((key) => {
      if (usedKeys[key]) {
        didConflict = true;
      }
      usedKeys[key] = true;
    });
    if (!didConflict) {
      suggestedDelta = { ...suggestedDelta, ...r.delta };
      filteredResults.push(r);
    }
  }
  return {
    delta: suggestedDelta,
    results: filteredResults,
  };
};

const box = {
  centerX,
  centerY,
  createItem,
  fromItem,
  pick,
  create,
  pickTransformed,
};

export default box;
